import * as React from 'react';
import { useIntl } from 'react-intl';
import ReactDOM from 'react-dom';
import { navigate } from 'wouter/use-location';
import CmdIcon, { BlockUserIcon, UnblockUserIcon } from '@commander-services/cmd-icon';
import PermissionsService, { PERMISSION_PERSON } from '../../../Services/PermissionsService';
import { TEmployeeRecord } from '../interfaces';
import {
  ACTIVATE_USER,
  IS_ACTIVE,
  IS_BLOCKED,
  IS_INACTIVE,
  IS_PENDING,
  RESET_USER,
} from '../constants';
import { URL_EMPLOYEE } from '../../../router/constants';
import useAnalytics from '../../../hooks/useAnalytics';

interface IActionMenuProps<T extends TEmployeeRecord> {
  data: T;
  removeConfirmModalCallback: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  activateConfirmModalCallback: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  deactivateConfirmModalCallback: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  reactivateConfirmModalCallback: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  resetConfirmModalCallback: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  position: { top: number; left: number };
}

const ActionsMenu = React.forwardRef<HTMLDivElement, IActionMenuProps<TEmployeeRecord>>(
  (props, ref) => {
    const { formatMessage: f } = useIntl();
    // const queryClient = useQueryClient();
    // const filterForRequest = useRecoilValue<ICmdTableFilter>(
    //   filterForRequestAtomFamily(EMPLOYEE_TABLE_NAME)
    // );
    // const sortFilter = useRecoilValue<{ [key: string]: string }>(
    //   sortFilterAtomFamily(EMPLOYEE_TABLE_NAME)
    // );
    const { trackEvent } = useAnalytics();
    const handleDetail = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();
      trackEvent(['employee', 'click', 'Open edit form']);
      // props.showDetailModalCallback(true);
      navigate(`${URL_EMPLOYEE}/edit/${props.data.id}?customerId=${props.data.customer.id}`);
    };

    const menu = (
      <div
        className="ridebook-dropdown-menu dropdown-menu dropdown-menu-right dropdown-menu-custom fs-2"
        style={{
          display: 'block',
          width: '160px',
          position: 'absolute',
          top: props.position.top,
          left: props.position.left,
          zIndex: 1,
        }}
        aria-labelledby="dropdown-actions-1"
        ref={ref}
      >
        {props.data && (
          <div>
            {PermissionsService.hasAccess(PERMISSION_PERSON, ['read'], props.data.customer.id) && (
              <a
                data-cy="action-detail"
                href="#"
                title={f({ id: 'table_list.actions.detail' })}
                className="px-3 py-2 d-flex align-items-center text-decoration-none cmd-table-text-gray"
                onClick={handleDetail}
                id={`action-detail-${props.data.id}`}
              >
                <i className="cmd-icons cmd-icon-detail mr-2 fs-4" />
                {f({ id: 'table_list.actions.detail' })}
              </a>
            )}
            {PermissionsService.hasAccess(PERMISSION_PERSON, ['delete'], props.data.customer.id) &&
              !props.data.hasUser &&
              !props.data.hasDriver && (
                <a
                  data-cy="action-detail"
                  href="#"
                  title={f({ id: 'table_list.actions.detail' })}
                  className="px-3 py-2 d-flex align-items-center text-decoration-none cmd-table-text-gray"
                  onClick={props.removeConfirmModalCallback}
                  id={`action-detail-${props.data.id}`}
                >
                  <i className="cmd-icons cmd-icon-bin mr-2 fs-4" />
                  {f({ id: 'table_list.actions.remove' })}
                </a>
              )}
            {PermissionsService.hasAccess(PERMISSION_PERSON, ['update'], props.data.customer.id) &&
              (props.data.status === IS_INACTIVE || props.data.status === IS_PENDING) && (
                <a
                  data-cy="action-activate"
                  href="#"
                  title={f({ id: 'table_list.actions.email' })}
                  className="px-3 py-2 d-flex align-items-center text-decoration-none cmd-table-text-gray"
                  id={`action-activate-${props.data.id}`}
                  onClick={props.activateConfirmModalCallback}
                >
                  <CmdIcon name={ACTIVATE_USER} />
                  {f({ id: 'table_list.actions.email' })}
                </a>
              )}
            {PermissionsService.hasAccess(PERMISSION_PERSON, ['update'], props.data.customer.id) &&
              (props.data.status === IS_ACTIVE || props.data.status === IS_PENDING) && (
                <a
                  data-cy="action-block"
                  href="#"
                  title={f({ id: 'table_list.actions.block.tooltip' })}
                  className="px-3 py-2 d-flex align-items-center text-decoration-none cmd-table-text-gray"
                  id={`action-deactivate-${props.data.id}`}
                  onClick={props.deactivateConfirmModalCallback}
                >
                  <BlockUserIcon style={{ marginRight: '8px' }} />
                  {f({ id: 'table_list.actions.block' })}
                </a>
              )}
            {PermissionsService.hasAccess(PERMISSION_PERSON, ['update'], props.data.customer.id) &&
              props.data.status === IS_BLOCKED && (
                <a
                  data-cy="action-reactivate"
                  href="#"
                  title={f({ id: 'table_list.actions.unblock.tooltip' })}
                  className="px-3 py-2 d-flex align-items-center text-decoration-none cmd-table-text-gray"
                  id={`action-discard-${props.data.id}`}
                  onClick={props.reactivateConfirmModalCallback}
                >
                  <UnblockUserIcon style={{ marginRight: '8px' }} />
                  {f({ id: 'table_list.actions.unblock' })}
                </a>
              )}
            {PermissionsService.hasAccess(PERMISSION_PERSON, ['update'], props.data.customer.id) &&
              props.data.status === IS_ACTIVE && (
                <a
                  data-cy="action-reset"
                  href="#"
                  title={f({ id: 'table_list.actions.reset' })}
                  className="px-3 py-2 d-flex align-items-center text-decoration-none cmd-table-text-gray"
                  id={`action-use-creation-settings-${props.data.id}`}
                  onClick={props.resetConfirmModalCallback}
                >
                  <CmdIcon name={RESET_USER} /> {f({ id: 'forgotPassword.passwordReset' })}
                </a>
              )}
          </div>
        )}
      </div>
    );

    return ReactDOM.createPortal(menu, document.body);
  }
);

export default ActionsMenu;
